import { ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-react'
import { uuid } from '@/utils'
import classNames from 'classnames'

import React, { useEffect, useState } from 'react'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

import { ContentLoader } from '@/components/atoms'

import { resizableGrid } from './tableUtils'

interface ClassesProps<Item> {
  column?: DataTableColumn<Item, any>
  data?: Item
}

/** Column keys that hold a thumbnail; on mobile they sit beside the row title instead of under it. */
const MEDIA_KEY = /^(image|thumbnail|logo|avatar|photo)$/i

export interface DataTableColumn<Item, ColumnKeys> {
  hidden?: boolean
  columnLabel?(): React.ReactNode
  classes?(props: ClassesProps<Item>): {
    column?: string
    cell?: string
  }
  cell(data: Item): React.ReactNode | string | null
  sorting?(): React.ReactNode
  accessorKey: ColumnKeys
  isAction?: boolean
}

export type DataTablePropsColumns<Item = any, ColumnKeys = any> = DataTableColumn<
  Item,
  ColumnKeys
>[]

interface DataTableProps<Item> {
  columns: DataTablePropsColumns<Item>
  data: Item[]
  classes?: {
    thead?: string
    tRow?: string
  }
  onRowClick?: (item?: Item) => void
  loading?: boolean
  staticHeaderRow?: React.ReactNode
  staticFooterRow?: React.ReactNode
}

function DataTable<Item>(props: Readonly<DataTableProps<Item>>) {
  const { columns, data, onRowClick, classes, loading, staticHeaderRow, staticFooterRow } = props
  const [tableId] = useState(uuid())
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

  const visibleColumns = columns.filter((column) => !column?.hidden)

  const renderTableFunctionalities = () => {
    const table: HTMLTableElement | null = document.getElementById(tableId) as HTMLTableElement

    if (table) {
      resizableGrid(tableId, table)
    }
  }

  useEffect(() => {
    renderTableFunctionalities()
  }, [tableId, columns])

  const toggleRow = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  if (loading && data?.length === 0) {
    return (
      <div className="flex items-center justify-center w-full min-h-[400px]">
        <ContentLoader />
      </div>
    )
  }

  if (!loading && data?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
        <p className="text-heading-md text-ink">No results found</p>
        <p className="mt-1 text-sm text-muted-foreground">Try changing the filters or search term.</p>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Desktop Table View */}
      <div className="hidden overflow-x-auto overflow-y-hidden scrollbar-hide md:block">
        <Table id={tableId}>
          <TableHeader className={classes?.thead}>
            <TableRow>
              {visibleColumns?.map((column, index) => (
                <TableHead
                  key={`col-${index + 1}`}
                  className={classNames(column?.classes?.({ column })?.column)}
                >
                  <div className="inline-flex items-center gap-1">
                    {column?.columnLabel?.()}
                    {column?.sorting?.()}
                  </div>
                </TableHead>
              ))}
            </TableRow>
            {staticHeaderRow && <TableRow>{staticHeaderRow}</TableRow>}
          </TableHeader>
          <TableBody>
            {data?.map((rowItem, dataItemIndex) => (
              <TableRow
                key={String((rowItem as any)?._id || dataItemIndex)}
                className={classNames(onRowClick && 'cursor-pointer', classes?.tRow)}
                onClick={() => onRowClick?.(rowItem)}
              >
                {visibleColumns?.map((columnItem, columnItemIndex) => (
                  <TableCell
                    key={`td-${columnItemIndex + 1}`}
                    className={classNames(columnItem?.classes?.({ data: rowItem })?.cell)}
                  >
                    {columnItem?.cell(rowItem)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          {staticFooterRow && (
            <tfoot>
              <TableRow>{staticFooterRow}</TableRow>
            </tfoot>
          )}
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="space-y-2 md:hidden">
        {data?.map((rowItem, index) => {
          const rowId = String((rowItem as any)?._id || index)
          const isExpanded = expandedRows[rowId]
          const actionColumn = visibleColumns.find((c) => c.isAction)
          const nonActionColumns = visibleColumns.filter((c) => !c.isAction)
          // A leading thumbnail column (image/logo) leads the card together with the next column (the name).
          const leadsWithMedia = MEDIA_KEY.test(String(nonActionColumns[0]?.accessorKey ?? '')) && nonActionColumns.length > 1
          const mediaColumn = leadsWithMedia ? nonActionColumns[0] : undefined
          const titleColumn = nonActionColumns[leadsWithMedia ? 1 : 0]
          const otherColumns = nonActionColumns.slice(leadsWithMedia ? 2 : 1)

          return (
            <Card
              key={rowId}
              className={classNames(
                'overflow-hidden',
                isExpanded && 'ring-1 ring-ring/40'
              )}
              onClick={() => onRowClick && onRowClick(rowItem)}
            >
              <CardContent className="p-0">
                <div className="p-4 flex items-start justify-between gap-4">
                  {mediaColumn && <div className="shrink-0">{mediaColumn.cell(rowItem)}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="mb-0.5 text-xs text-muted-foreground">
                      {titleColumn?.columnLabel?.()}
                    </div>
                    <div className="truncate text-sm font-medium text-ink">
                      {titleColumn?.cell(rowItem)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {actionColumn && (
                      <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        {actionColumn.cell(rowItem)}
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="bg-fill hover:bg-fill-hover"
                      onClick={(e) => toggleRow(rowId, e)}
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div
                  className={classNames(
                    'overflow-hidden border-t border-border-subtle bg-subdued transition-all duration-300 ease-in-out',
                    isExpanded ? 'max-h-[1000px] opacity-100 p-4' : 'max-h-0 opacity-0 p-0'
                  )}
                >
                  <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                    {otherColumns.map((col, cIdx) => (
                      <div key={cIdx} className="space-y-1">
                        <p className="text-xs text-muted-foreground">{col.columnLabel?.()}</p>
                        <div className="break-words text-sm text-ink">
                          {col.cell(rowItem)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export { DataTable }
